// Generado desde design-system/components/motion/ThemeToggle.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { useRef } from 'react';
import { flushSync } from 'react-dom';
import { cn } from '../../lib/cn';
import { DUR, EASE, injectStyles, prefersReducedMotion } from './motion-utils';
import './motion-styles';
/*
 * ThemeToggle — cambio claro/oscuro sin parpadeo.
 * Ícono sol ⇄ luna por transform (los rayos se recogen y una sombra circular
 * «muerde» el núcleo). Con View Transitions (si existen) el tema nuevo se revela
 * en un círculo que nace del botón; sin soporte o con movimiento reducido: cambio
 * instantáneo. La clase dbm-vt-theme acota el CSS de la transición a este uso.
 */
injectStyles('db-motion-theme-toggle', `.dbm-tt svg { overflow: visible; }
.dbm-tt .core { transform-box: fill-box; transform-origin: center; transition: transform .6s var(--dbm-spring); }
.dbm-tt .bite { transform-box: view-box; transition: transform .6s var(--db-ease-out); transform: translate(9px, -9px); }
.dbm-tt .rays { transform-box: fill-box; transform-origin: center; transition: transform .6s var(--db-ease-out), opacity .3s var(--db-ease-out); }
.dbm-tt[data-theme-state="dark"] .core { transform: scale(1.55); }
.dbm-tt[data-theme-state="dark"] .bite { transform: translate(0, 0); }
.dbm-tt[data-theme-state="dark"] .rays { transform: rotate(-60deg) scale(.4); opacity: 0; }
html.dbm-vt-theme::view-transition-old(root), html.dbm-vt-theme::view-transition-new(root) { animation: none; mix-blend-mode: normal; }
@media (prefers-reduced-motion: reduce) { .dbm-tt .core, .dbm-tt .bite, .dbm-tt .rays { transition-duration: .01s; } }`);
export function ThemeToggle({ theme = 'light', onChange, reveal = true, size = 'md', className, labels = { toDark: 'Cambiar a tema oscuro', toLight: 'Cambiar a tema claro' } }) {
    const btn = useRef(null);
    const dark = theme === 'dark';
    const uid = useRef(`m${Math.random().toString(36).slice(2, 8)}`).current;
    const label = dark ? labels.toLight : labels.toDark;
    const toggle = () => {
        const next = dark ? 'light' : 'dark';
        const doc = typeof document !== 'undefined' ? document : null;
        if (!reveal || !doc?.startViewTransition || prefersReducedMotion() || !btn.current) {
            onChange?.(next);
            return;
        }
        const r = btn.current.getBoundingClientRect();
        const x = r.left + r.width / 2;
        const y = r.top + r.height / 2;
        const end = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
        const html = doc.documentElement;
        html.classList.add('dbm-vt-theme');
        const vt = doc.startViewTransition(() => {
            flushSync(() => onChange?.(next));
        });
        vt.ready
            .then(() => {
            html.animate({ clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${end}px at ${x}px ${y}px)`] }, { duration: DUR.close + 120, easing: EASE.out, pseudoElement: '::view-transition-new(root)' });
        })
            .catch(() => { });
        vt.finished.finally(() => html.classList.remove('dbm-vt-theme'));
    };
    const dims = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
    return (<button ref={btn} type="button" onClick={toggle} aria-label={label} title={label} aria-pressed={dark} data-theme-state={dark ? 'dark' : 'light'} className={cn('dbm-tt dbm-press grid place-items-center rounded-full text-ink transition-colors duration-base hover:text-accent-ink', 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ring', dims, className)} style={{ '--dbm-press': 0.92 }}>
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
        <mask id={uid}>
          <rect x="-4" y="-4" width="32" height="32" fill="white"/>
          <circle className="bite" cx="15.4" cy="8.6" r="3.9" fill="black"/>
        </mask>
        <circle className="core" cx="12" cy="12" r="4.4" fill="currentColor" mask={`url(#${uid})`}/>
        <g className="rays" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M12 2.5v1.8M12 19.7v1.8M2.5 12h1.8M19.7 12h1.8M5.3 5.3l1.3 1.3M17.4 17.4l1.3 1.3M5.3 18.7l1.3-1.3M17.4 6.6l1.3-1.3"/>
        </g>
      </svg>
    </button>);
}
